import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dummyImage from "../images/profilepic.png";
import { fetchAttendanceStatus } from "../redux/actions/attendanceActions";
import { fetchProfile } from "../redux/actions/profileActions";
import { logoutEmployee } from "../redux/actions/authActions";
import AttendModal from "./Attend/AttendModal";
import moment from "moment";

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("checkin"); // "checkin" or "checkout"
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [permissionStatus, setPermissionStatus] = useState({
    location: null,
    camera: null,
  });

  const { data: profileData, loading: profileLoading, error: profileError } = useSelector((state) => state.profile);
  const { attendanceStatus, loading: attendanceLoading, error: attendanceError } = useSelector((state) => state.attendance);
  console.log("profileData>>>", profileData);

  useEffect(() => {
    dispatch(fetchProfile());
    dispatch(fetchAttendanceStatus());
    checkPermissions();
  }, [dispatch]);

  // Refresh attendance status when modal closes
  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalType("checkin");
    // Refresh attendance status to get updated data
    dispatch(fetchAttendanceStatus());
  };

  const handleLogout = () => {
    dispatch(logoutEmployee());
    navigate("/login");
  };

  // Check if error is related to invalid token
  const isInvalidTokenError = (error) => {
    if (!error) return false;
    const errorMsg = error.toLowerCase();
    return errorMsg.includes('invalid token') || 
           errorMsg.includes('unauthorized') || 
           errorMsg.includes('token expired') ||
           errorMsg.includes('authentication failed') ||
           errorMsg.includes('401');
  };

  // Check if any critical error exists
  const hasInvalidTokenError = isInvalidTokenError(profileError) || isInvalidTokenError(attendanceError);
  const hasCriticalError = profileError || attendanceError;
  const isLoadingData = profileLoading || attendanceLoading;

  const today = moment().format("ddd, MMM D");

  const name = profileData?.name || "Unknown";
  const designation = profileData?.designation || "Unknown";
  const municipality = profileData?.municipality || "N/A";
  const municipalityAnother = profileData?.municipality_another || [];
  const phone = profileData?.phone || "N/A";

  // Function to get display text for municipalities
  const getMunicipalityDisplayText = () => {
    let displayText = municipality;
    
    if (municipalityAnother && municipalityAnother.length > 0) {
      const additionalMunicipalities = municipalityAnother.map(item => item.name).join(", ");
      displayText = `${municipality}, ${additionalMunicipalities}`;
    }
    
    return displayText;
  };

  const checkInPhoto = attendanceStatus?.check_in_photo;
  const checkOutPhoto = attendanceStatus?.check_out_photo;
  const image =
    checkInPhoto || checkOutPhoto || profileData?.photo || dummyImage;

  // Enhanced attendance status logic matching React Native
  const getAttendanceStatusText = () => {
    const { status, attendance_status_text, is_attendance_given } =
      attendanceStatus || {};

    if (
      attendance_status_text === "Clocked Out Other" ||
      attendance_status_text === "Clocked Out Outside" ||
      attendance_status_text === "Clocked Out Inside"
    ) {
      return "Clocked Out";
    }

    if (status === "pending") {
      return "Pending...";
    }

    return "Clocked In";
  };

  const getAttendanceColor = () => {
    const { status } = attendanceStatus || {};
    return status === "pending" ? "text-red-500" : "text-green-500";
  };

  const appVersion =
    profileData?.app_info?.find((item) => item.name === "App Version")?.value ||
    "1.0";

  // Permission checking functions
  const checkPermissions = async () => {
    await checkLocationPermission();
    await checkCameraPermission();
  };

  const checkLocationPermission = async () => {
    if (!navigator.geolocation) {
      setPermissionStatus((prev) => ({ ...prev, location: "not-supported" }));
      return false;
    }

    try {
      // Test location access
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });
      setPermissionStatus((prev) => ({ ...prev, location: permission.state }));
      return permission.state === "granted";
    } catch (error) {
      setPermissionStatus((prev) => ({ ...prev, location: "denied" }));
      return false;
    }
  };

  const checkCameraPermission = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissionStatus((prev) => ({ ...prev, camera: "not-supported" }));
      return false;
    }

    try {
      const permission = await navigator.permissions.query({ name: "camera" });
      setPermissionStatus((prev) => ({ ...prev, camera: permission.state }));
      return permission.state === "granted";
    } catch (error) {
      setPermissionStatus((prev) => ({ ...prev, camera: "denied" }));
      return false;
    }
  };

  const requestLocationPermission = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPermissionStatus((prev) => ({ ...prev, location: "granted" }));
          resolve(position);
        },
        (error) => {
          setPermissionStatus((prev) => ({ ...prev, location: "denied" }));
          let errorMessage = "Unable to fetch location. ";

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Location permission denied.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location not available.";
              break;
            case error.TIMEOUT:
              errorMessage += "Location request timed out.";
              break;
            default:
              errorMessage += "Please try again.";
          }

          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
        }
      );
    });
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately as we just needed permission
      stream.getTracks().forEach((track) => track.stop());
      setPermissionStatus((prev) => ({ ...prev, camera: "granted" }));
      return true;
    } catch (error) {
      setPermissionStatus((prev) => ({ ...prev, camera: "denied" }));
      return false;
    }
  };

  // Determine if user should check in or check out
  const determineAttendanceAction = () => {
    const { status, is_attendance_given } = attendanceStatus || {};

    // User needs to check out if they are present (checked in)
    if (status === "present" && (is_attendance_given === 1 || is_attendance_given === 2 || is_attendance_given === 3)) {
      return "checkout";
    }

    // User needs to check in if they haven't checked in yet or are pending
    if (status === "pending" && is_attendance_given === 0) {
      return "checkin";
    }

    // Default to checkin for other cases
    return "checkin";
  };

  const handleClockAction = async () => {
    try {
      setLoading(true);
      setLoadingMessage("Checking permissions...");

      // Check permissions before proceeding
      if (
        permissionStatus.location === "denied" ||
        permissionStatus.location === "not-supported"
      ) {
        const confirmGrant = window.confirm(
          "Location Permission Required\n\nPlease grant location permission to mark attendance.\n\nClick OK to try again."
        );
        if (confirmGrant) {
          await checkLocationPermission();
        }
        setLoading(false);
        setLoadingMessage("");
        return;
      }

      if (
        permissionStatus.camera === "denied" ||
        permissionStatus.camera === "not-supported"
      ) {
        const confirmGrant = window.confirm(
          "Camera Permission Required\n\nPlease grant camera permission to mark attendance.\n\nClick OK to try again."
        );
        if (confirmGrant) {
          await checkCameraPermission();
        }
        setLoading(false);
        setLoadingMessage("");
        return;
      }

      // Check if there's a running task for checkout
      const { status, is_attendance_given, is_task_running } = attendanceStatus || {};
      
      if (status === "present" && is_task_running) {
        setLoading(false);
        setLoadingMessage("");
        const confirmNavigate = window.confirm(
          "Warning!\n\nPlease End the running task\n\nWould you like to go to Daily Task page?"
        );
        if (confirmNavigate) {
          // Navigate to DailyTask page - you'll need to implement this navigation
          console.log("Navigate to Daily Task page");
        }
        return;
      }

      // Determine action type and open modal
      const actionType = determineAttendanceAction();
      setModalType(actionType);
      
      setLoading(false);
      setLoadingMessage("");
      setIsModalOpen(true);

    } catch (error) {
      setLoading(false);
      setLoadingMessage("");
      console.error("Permission check error:", error);
      alert(error.message || "Failed to check permissions. Please try again.");
    }
  };

  const getClockButtonText = () => {
    const { status, is_attendance_given } = attendanceStatus || {};

    if (status === "pending" && is_attendance_given === 0) return "Clock In";
    if (status === "present" && is_attendance_given === 1) return "Clock Out";
    if (status === "present" && is_attendance_given === 2) return "Clock Out";
    if (status === "pending" && is_attendance_given === 2)
      return "Clock in pending...";
    if (status === "pending" && is_attendance_given === 3)
      return "Clock in pending...";
    if (status === "present" && is_attendance_given === 3) return "Clock Out";

    return "Clock In";
  };

  const getClockButtonColor = () => {
    const { status, is_attendance_given } = attendanceStatus || {};

    if (status === "pending" && is_attendance_given === 0)
      return "bg-green-500";
    if (
      status === "pending" &&
      (is_attendance_given === 2 || is_attendance_given === 3)
    )
      return "bg-green-500 opacity-50";
    if (status === "present") return "bg-orange-500";

    return "bg-gray-500";
  };

  const isClockButtonDisabled = () => {
    const { status, is_attendance_given } = attendanceStatus || {};
    return (
      (status === "pending" && is_attendance_given === 2) ||
      (status === "pending" && is_attendance_given === 3)
    );
  };

  const shouldShowClockButton = () => {
    const { attendance_status_text, is_attendence_allowed } =
      attendanceStatus || {};

    return !(
      attendance_status_text === "Clocked Out Outside" ||
      attendance_status_text === "Clocked Out Inside" ||
      attendance_status_text === "Clocked Out Other" ||
      is_attendence_allowed === false
    );
  };

  // Show loading state
  if (isLoadingData && !hasCriticalError) {
    return (
      <div className="h-full bg-[#2f3e51] flex flex-col">
        {/* Header */}
        <div className="w-full bg-sky-400 text-white py-3 px-6 flex justify-between items-center text-lg font-bold">
          <span>Home</span>
          <span className="text-xs">V{appVersion}</span>
        </div>

        {/* Loading Content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400 mx-auto mb-4"></div>
            <p className="text-white font-medium">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state with logout option for invalid token or other critical errors
  if (hasCriticalError) {
    return (
      <div className="h-full bg-[#2f3e51] flex flex-col">
        {/* Header */}
        <div className="w-full bg-sky-400 text-white py-3 px-6 flex justify-between items-center text-lg font-bold">
          <span>Home</span>
          <span className="text-xs">V{appVersion}</span>
        </div>

        {/* Error Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-center">
            {hasInvalidTokenError ? (
              <>
                {/* Invalid Token Error */}
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Session Expired</h3>
                  <p className="text-gray-600 mb-4">
                    Your login session has expired. Please log in again to continue.
                  </p>
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded border">
                    {profileError || attendanceError}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full bg-sky-400 hover:bg-sky-500 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                >
                  Go to Login
                </button>
              </>
            ) : (
              <>
                {/* General Error */}
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded border mb-4">
                    {profileError || attendanceError}
                  </p>
                </div>
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => {
                      dispatch(fetchProfile());
                      dispatch(fetchAttendanceStatus());
                    }}
                    className="w-full bg-sky-400 hover:bg-sky-500 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#2f3e51] flex flex-col relative">
      {/* Header */}
      <div className="w-full bg-sky-400 text-white py-3 px-6 flex justify-between items-center text-lg font-bold">
        <span>Home</span>
        <span className="text-xs">V{appVersion}</span>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400 mx-auto mb-4"></div>
            <p className="text-gray-700">{loadingMessage}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center px-4 overflow-auto">
        <div className="bg-white rounded-xl p-4 shadow-md mt-6 w-full max-w-md flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-bold">{name}</h2>
            <p className="text-sm text-gray-600">{phone}</p>
            <p className="text-yellow-600 font-semibold text-sm">
              {getMunicipalityDisplayText()}
            </p>
            <p className="text-sm">
              Designation : <b>{designation}</b>
            </p>
            <p className="text-sm">
              Attendance :{" "}
              <span className={`${getAttendanceColor()} font-medium`}>
                {getAttendanceStatusText()}
              </span>
            </p>
            <p className="text-sm">
              Today :{" "}
              <span className="text-green-500 font-medium">{today}</span>
            </p>

            {/* Permission Status Indicators */}
            {(permissionStatus.location === "denied" ||
              permissionStatus.camera === "denied") && (
              <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  ⚠️ Permissions needed for attendance marking
                </p>
                {permissionStatus.location === "denied" && (
                  <p className="text-xs text-yellow-700">
                    • Location access required
                  </p>
                )}
                {permissionStatus.camera === "denied" && (
                  <p className="text-xs text-yellow-700">
                    • Camera access required
                  </p>
                )}
              </div>
            )}

            {/* Debug Info - Remove in production */}
            {/* {process.env.NODE_ENV === 'development' && attendanceStatus && (
              <div className="mt-2 p-2 bg-gray-100 border rounded text-xs">
                <p><strong>Debug Info:</strong></p>
                <p>Status: {attendanceStatus.status}</p>
                <p>Is Attendance Given: {attendanceStatus.is_attendance_given}</p>
                <p>Action: {determineAttendanceAction()}</p>
              </div>
            )} */}
          </div>

          <div className="ml-2">
            <div className="w-24 h-32 rounded-xl border-2 border-sky-400 overflow-hidden">
              <img
                src={image}
                className="w-full h-full object-cover"
                alt="profile"
              />
            </div>
          </div>
        </div>

        {/* Clock In/Out Button */}
        {shouldShowClockButton() && (
          <button
            onClick={handleClockAction}
            disabled={isClockButtonDisabled()}
            className={`${getClockButtonColor()} mt-6 px-8 py-3 rounded-xl text-white text-lg font-semibold w-full max-w-md transition-all duration-200 ${
              isClockButtonDisabled()
                ? "cursor-not-allowed"
                : "hover:opacity-90"
            }`}
          >
            {getClockButtonText()}
          </button>
        )}
      </div>

      {/* Single Attendance Modal - conditionally rendered with correct type */}
      {isModalOpen && (
        <AttendModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          type={modalType}
        />
      )}
    </div>
  );
};

export default HomePage;